<template>
  <SalesPageShell
    :title="text.page.title"
    :subtitle="text.page.subtitle"
    :actions="headerActions"
    @action="onHeaderAction"
  >
    <template #metrics>
      <section class="metrics-grid">
        <SalesKpiCard
          v-for="metric in kpiCards"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :value-route="metric.valueRoute"
          :value-link-class="metric.valueLinkClass"
          :value-scale="metric.valueScale"
          :truncate-value="metric.truncateValue"
          :value-meta="metric.valueMeta"
          :extra-values="metric.extraValues"
          :value-segments="metric.valueSegments"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>
    </template>

    <template #toolbar>
      <SharedSalesToolbar
        mode="sales"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />
    </template>

    <template #tabs>
      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />
    </template>

    <template #table>
      <SharedSalesTable
        :rows="paginatedRows"
        :loading="tableLoading"
        :empty-text="text.page.table.empty"
        :pagination="salesPagination"
        :show-customer-column="true"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @change-sale-status="handleChangeSaleStatus"
        @edit-sale="handleEditSale"
        @delete-sale="handleDeleteSale"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
      />
    </template>

    <template #modal>
      <div>
        <SaleModal
          :show="showSaleEditModal"
          :mode="saleModalMode"
          :initial-data="saleModalInitialData"
          :submitting="saleSubmitting"
          :advertising-enabled="isAdvertisingModuleActive"
          @close="closeSaleModal"
          @delete="handleDeleteSaleFromModal"
          @submit="handleCreateSale"
        />

        <ConfirmActionModal
          :show="isDeleteConfirmModalOpen"
          :title="text.page.confirm.delete.title"
          :subtitle="text.page.confirm.delete.subtitle"
          :message="deleteConfirmMessage"
          :confirm-text="text.page.confirm.delete.confirm"
          @cancel="closeDeleteConfirmModal"
          @confirm="confirmDeleteSale"
        />

        <SaleStatusModal
          :show="isStatusModalOpen"
          :sale-name="resolveSelectedSaleName()"
          :current-status-label="resolveSelectedSaleStatusLabel()"
          :statuses="statusOptions"
          :selected-status-id="resolveSelectedSaleStatusId()"
          :submitting="statusModalSubmitting"
          @close="closeStatusModal"
          @submit="confirmUpdateSaleStatus"
        />
      </div>
    </template>
  </SalesPageShell>
</template>

<script>
import SalesKpiCard from '@/components/sales/SalesKpiCard.vue';
import SalesPageShell from '@/components/sales/SalesPageShell.vue';
import SharedSalesToolbar from '@/components/sales/shared/SalesToolbar.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import SharedSalesTable from '@/components/sales/shared/SalesTable.vue';
import SaleStatusModal from '@/components/sales/shared/SaleStatusModal.vue';
import SaleModal from '@/components/sales/SaleModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import salesService from '@/services/sales';
import { formatCurrency } from '@/utils/numberFormat';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractPayload,
} from '@/utils/dataNormalization';
import { CAPABILITIES_UPDATED_EVENT } from '@/utils/capabilities';
import {
  createDefaultSalesToolbarFilters,
  normalizeSalesToolbarFilters,
  filterSalesRowsLocal,
} from '@/composables/sales/useSalesFilters';
import {
  buildSalesStatusTabs,
  filterRowsByStatus,
} from '@/composables/sales/useSalesStatusTabs';
import {
  createInitialSalesMetricsValues,
  mapSalesMetricsPayload,
  buildSalesMetricsFromRows,
  buildSalesKpiCards,
} from '@/composables/sales/useSalesMetrics';
import {
  buildCreateSaleRequestData,
  buildUpdateSaleRequestData,
} from '@/composables/sales/useSalesCreateModal';
import { fetchSalesSnapshot } from '@/composables/sales/useSalesList';
import {
  createCustomerDetailSaleActionState,
  createCustomerDetailSaleActionComputed,
  createCustomerDetailSaleActionMethods,
} from '@/composables/customers/customerDetail/useCustomerDetailSaleActions';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';

const { catalog: text } = useDomainText('sales');

const saleActionComputed = createCustomerDetailSaleActionComputed({
  entityLabel: text.customerDetail.sales.entityLabel,
});

const saleActionMethods = createCustomerDetailSaleActionMethods({
  readService: salesService,
  writeService: salesService,
  buildUpdateRequestData: buildUpdateSaleRequestData,
  messages: {
    loadStatusOptionsError: text.page.actionMessages.loadStatusOptionsError,
    updateStatusError: text.page.actionMessages.updateStatusError,
    updateStatusSuccess: text.page.actionMessages.updateStatusSuccess,
    updateError: text.page.actionMessages.updateError,
    updateSuccess: text.page.actionMessages.updateSuccess,
    loadEditError: text.page.actionMessages.loadEditError,
    deleteError: text.page.actionMessages.deleteError,
    deleteSuccess: text.page.actionMessages.deleteSuccess,
  },
});

export default {
  name: 'SalesPage',
  components: {
    SalesKpiCard,
    SalesPageShell,
    SharedSalesToolbar,
    StatusTabs,
    SharedSalesTable,
    SaleStatusModal,
    SaleModal,
    ConfirmActionModal,
  },
  data() {
    return {
      text,
      headerActionsBase: [
        {
          key: 'new-sale',
          label: text.page.headerActions.newSale,
          icon: 'fa-plus',
          variant: 'primary',
        },
        {
          key: 'import-sales',
          label: text.page.headerActions.importSales,
          icon: 'fa-upload',
          variant: 'secondary',
        },
      ],
      headerActions: [],
      tableLoading: false,
      salesAllRows: [],
      salesRows: [],
      activeStatusTab: 'all',
      toolbarSearch: '',
      toolbarFilters: createDefaultSalesToolbarFilters(),
      appliedToolbarSearch: '',
      appliedToolbarFilters: createDefaultSalesToolbarFilters(),
      listRequestToken: 0,
      currentPage: 1,
      pageSize: 30,
      sortField: 'sale_date',
      sortDirection: 'desc',
      ...createCustomerDetailSaleActionState(),
      saleModalMode: 'create',
      lastFetchParamsKey: '',
      hasLoadedListOnce: false,
      metricsValues: createInitialSalesMetricsValues(),
    };
  },
  computed: {
    normalizedRows() {
      return Array.isArray(this.salesRows) ? this.salesRows : [];
    },
    statusTabs() {
      return buildSalesStatusTabs(this.normalizedRows);
    },
    filteredRows() {
      return filterRowsByStatus(this.normalizedRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.filteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        sale_date: (row) => new Date(row?.sale_date || row?.date || 0).getTime(),
        customer: (row) => String(row?.company_name || row?.customer_name || row?.cliente || '').trim().toLowerCase(),
        sale_name: (row) => String(row?.sale_name || row?.project || row?.name || row?.code || '').trim().toLowerCase(),
        service: (row) => String(row?.service_name || row?.service || row?.service_type || '').trim().toLowerCase(),
        subtotal: (row) => Number(row?.subtotal || 0),
        balance: (row) => Number(row?.balance ?? row?.saldo ?? 0),
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    salesPagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
    kpiCards() {
      return buildSalesKpiCards(this.metricsValues, formatCurrency);
    },
    ...saleActionComputed,
  },
  mounted() {
    this.refreshHeaderActions();
    window.addEventListener(CAPABILITIES_UPDATED_EVENT, this.refreshHeaderActions);
    this.fetchSales();
  },
  beforeUnmount() {
    window.removeEventListener(CAPABILITIES_UPDATED_EVENT, this.refreshHeaderActions);
  },
  methods: {
    refreshHeaderActions() {
      const can = typeof this.$can === 'function' ? this.$can : () => false;
      this.headerActions = this.headerActionsBase.filter((action) => {
        if (action?.key === 'new-sale') {
          return can('sales.button_new') || can('sales.create');
        }

        if (action?.key === 'import-sales') {
          return can('sales.list');
        }

        return true;
      });
    },
    onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim();

      if (actionKey === 'new-sale') {
        this.openCreateSaleModal();
      }

      if (actionKey === 'import-sales') {
        this.$toast?.info(this.text.page.toasts.importPending);
      }
    },
    openCreateSaleModal() {
      this.saleModalMode = 'create';
      this.saleModalInitialData = null;
      this.editingSaleId = 0;
      this.showSaleEditModal = true;
    },
    closeSaleModal(force = false) {
      if (!force && this.saleSubmitting) {
        return;
      }

      this.showSaleEditModal = false;

      if (force) {
        this.saleModalMode = 'create';
        this.saleModalInitialData = null;
        this.editingSaleId = 0;
      }
    },
    async handleCreateSale(payload) {
      this.saleSubmitting = true;

      try {
        const isEditMode = this.saleModalMode === 'edit' && Number(this.editingSaleId || 0) > 0;
        const requestData = isEditMode
          ? buildUpdateSaleRequestData(payload)
          : buildCreateSaleRequestData(payload);

        const response = isEditMode
          ? await salesService.update(Number(this.editingSaleId), requestData)
          : await salesService.store(requestData);

        const apiErrorMessage = extractApiErrorMessage(
          response,
          isEditMode
            ? this.text.page.toasts.updateError
            : this.text.page.toasts.createError,
        );

        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        const createdSale = extractPayload(response);
        const createdSaleId = Number(createdSale?.id || createdSale?.sale_id || 0);

        this.$toast?.success(
          extractApiSuccessMessage(
            response,
            isEditMode
              ? this.text.page.toasts.updateSuccess
              : this.text.page.toasts.createSuccess,
          )
        );
        this.closeSaleModal(true);

        if (!isEditMode && createdSaleId > 0) {
          this.$router.push({ name: 'saleDetail', params: { saleId: createdSaleId } }).catch(() => {});
          return;
        }

        if (!isEditMode) {
          this.activeStatusTab = 'all';
        }

        await this.fetchSales(true);
      } catch (error) {
        this.$toast?.error(
          error?.response?.data?.message
            || (this.saleModalMode === 'edit'
              ? this.text.page.toasts.updateError
              : this.text.page.toasts.createError)
        );
      } finally {
        this.saleSubmitting = false;
      }
    },
    ensureValidPage() {
      this.currentPage = ensureValidPage(this.currentPage, this.totalPages);
    },
    buildRemoteListParams() {
      const normalized = normalizeSalesToolbarFilters(this.appliedToolbarFilters);

      return {
        date_start: String(normalized.date_start || '').trim(),
        date_end: String(normalized.date_end || '').trim(),
        service: normalized.service,
        status: normalized.status,
        customer: normalized.customer,
        seller: normalized.seller,
        owner: normalized.owner,
        area: normalized.area,
      };
    },
    buildFetchParamsKey() {
      return JSON.stringify(this.buildRemoteListParams());
    },
    syncRowsFromAppliedState() {
      this.salesRows = filterSalesRowsLocal(
        this.salesAllRows,
        this.appliedToolbarSearch,
        this.appliedToolbarFilters,
      );
      this.ensureValidPage();
    },
    async fetchSales(force = false) {
      const paramsKey = this.buildFetchParamsKey();

      if (!force && this.tableLoading && this.lastFetchParamsKey === paramsKey) {
        return;
      }

      if (!force && this.hasLoadedListOnce && this.lastFetchParamsKey === paramsKey) {
        return;
      }

      this.lastFetchParamsKey = paramsKey;
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;
      const requestParams = this.buildRemoteListParams();

      try {
        const snapshot = await fetchSalesSnapshot(salesService, requestParams);

        if (requestToken === this.listRequestToken) {
          this.salesAllRows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];

          this.syncRowsFromAppliedState();

          const metricsPayload = snapshot?.metrics && typeof snapshot.metrics === 'object'
            ? snapshot.metrics
            : null;

          if (metricsPayload && Object.keys(metricsPayload).length > 0) {
            this.metricsValues = mapSalesMetricsPayload(metricsPayload);
          } else {
            this.syncMetricsFromRows();
          }
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.salesAllRows = [];
          this.salesRows = [];
          this.metricsValues = createInitialSalesMetricsValues();
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
          this.hasLoadedListOnce = true;
        }
      }
    },
    syncMetricsFromRows() {
      this.metricsValues = buildSalesMetricsFromRows(this.salesAllRows);
    },
    handleToolbarSearch(value) {
      const nextSearch = String(value || '');
      this.toolbarSearch = nextSearch;
      this.appliedToolbarSearch = nextSearch;
      this.currentPage = 1;
      this.syncRowsFromAppliedState();
    },
    handleToolbarFilters(filters, meta = {}) {
      const previousDateStart = String(this.toolbarFilters?.date_start || '').trim();
      const previousDateEnd = String(this.toolbarFilters?.date_end || '').trim();
      const normalized = normalizeSalesToolbarFilters(filters);
      this.toolbarFilters = normalized;

      if (Object.prototype.hasOwnProperty.call(normalized, 'search')) {
        this.toolbarSearch = String(normalized.search || '');
      }

      const nextDateStart = String(normalized.date_start || '').trim();
      const nextDateEnd = String(normalized.date_end || '').trim();
      const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;
      const trigger = String(meta?.trigger || '').trim();

      if (dateChanged && trigger === 'date') {
        this.appliedToolbarFilters = normalized;
        this.appliedToolbarSearch = String(this.toolbarSearch || '');
        this.currentPage = 1;
        this.fetchSales(true);
      }
    },
    applyToolbarFilters(payload = {}) {
      const search = Object.prototype.hasOwnProperty.call(payload, 'search')
        ? String(payload.search || '')
        : String(this.toolbarSearch || '');

      const filters = Object.prototype.hasOwnProperty.call(payload, 'filters')
        ? normalizeSalesToolbarFilters(payload.filters)
        : normalizeSalesToolbarFilters(this.toolbarFilters);

      this.toolbarSearch = search;
      this.toolbarFilters = filters;
      this.appliedToolbarSearch = search;
      this.appliedToolbarFilters = filters;
      this.currentPage = 1;
      this.fetchSales(true);
    },
    selectStatusTab(statusKey) {
      this.activeStatusTab = statusKey;
      this.currentPage = 1;
      this.ensureValidPage();
    },
    handlePageChange(nextPage) {
      this.currentPage = Number(nextPage || 1);
      this.ensureValidPage();
    },
    handleSortChange(payload = {}) {
      const nextSort = resolveSortChange(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        payload,
        ['sale_date', 'customer', 'sale_name', 'service', 'subtotal', 'balance'],
      );

      if (nextSort.field === this.sortField && nextSort.direction === this.sortDirection) {
        return;
      }

      this.sortField = nextSort.field;
      this.sortDirection = nextSort.direction;
      this.currentPage = 1;
      this.ensureValidPage();
    },
    ...saleActionMethods,
  },
};
</script>
